import type { BoardSummary, HallMonth } from "../shared/types";

/**
 * Which looks a row may write into src/appearance.ts (owner, 2026-10-04: "The peon of the month
 * card on the done screen seems to be behind on clothing updates"). Rows that carry a peon's look
 * (TopPeon on the board summary, HallMonth on the Hall) arrive from requests that may have started
 * before this device changed that peon's look; such a row must not paint the old clothes back over
 * the new ones.
 *
 * So every board summary and Hall request takes a generation when it starts (nextLookGeneration),
 * and the character editor's change marks the name at the generation current when it was made and
 * again when the server took it (markLocalLook). A row updates the store only when its request's
 * generation is later than the name's mark; a peon never changed on this device has no mark, so
 * their rows always land and another device's change shows with the next summary.
 *
 * A leaf module (src/api.ts and src/appearance.ts both import it, and src/appearance.ts imports
 * src/api.ts): the store registers itself as the sink.
 */
export interface RowLook {
  name: string;
  shirt?: unknown;
  hair?: unknown;
  beard?: unknown;
  accessories?: unknown;
  badges?: unknown;
}

let generation = 0;
const marks = new Map<string, number>();
let sink: ((row: RowLook) => void) | null = null;

function key(name: string): string {
  return name.trim().toLowerCase();
}

/** A board summary or Hall request is starting: its rows are this new generation. */
export function nextLookGeneration(): number {
  generation += 1;
  return generation;
}

/** This device changed `name`'s look now: rows from requests already started no longer apply. */
export function markLocalLook(name: string): void {
  const k = key(name);
  if (k) marks.set(k, generation);
}

/** Whether a row from a request of generation `gen` is at least as new as this device's own change. */
export function rowIsFresh(name: string, gen: number): boolean {
  const mark = marks.get(key(name));
  return mark === undefined || gen > mark;
}

/** The store (src/appearance.ts) takes the rows that pass. */
export function setLookRowSink(fn: ((row: RowLook) => void) | null): void {
  sink = fn;
}

/** Hands each fresh row to the store; one that predates this device's change is dropped. */
export function lookRowsArrived(rows: ReadonlyArray<RowLook | null | undefined>, gen: number): void {
  if (!sink) return;
  for (const row of rows) if (row && typeof row.name === "string" && rowIsFresh(row.name, gen)) sink(row);
}

/** The rows a board summary carries a look on: the leader, the crown's peon and the standings. */
export function summaryLookRows(summary: Pick<BoardSummary, "top" | "crown" | "standings">): Array<RowLook | null | undefined> {
  return [summary.top, summary.crown?.peon, ...(summary.standings ?? [])];
}

/** A board summary as it arrives: its looks go to the store by the rule above. Returns it unchanged. */
export function noteSummaryLooks<T extends Pick<BoardSummary, "top" | "crown" | "standings">>(summary: T, gen: number): T {
  if (summary && typeof summary === "object") lookRowsArrived(summaryLookRows(summary), gen);
  return summary;
}

/** The Hall's months as they arrive, by the same rule. Returns them unchanged. */
export function noteHallLooks<T extends HallMonth[]>(months: T, gen: number): T {
  if (Array.isArray(months)) lookRowsArrived(months, gen);
  return months;
}

/** Tests only. */
export function resetLookRowsForTests(): void {
  generation = 0;
  marks.clear();
}
